import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, Button } from 'react-bootstrap';
import { FiMapPin, FiHeart } from 'react-icons/fi';
import { FaHeart } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { PropertyThumbnail } from './OptimizedImage';
import { toggleFavorite, isLocalFavorite, getFavorites } from '../services/favorites';
import { toast } from 'react-toastify';

const PropertyCard = ({ property, showHost = false, showDistance = false, compact = false }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const primaryImage = property.images?.find(img => img.is_primary)?.image_url ||
                       property.images?.[0]?.image_url;

  // Check if property is favorited
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (isAuthenticated) {
        try {
          const result = await getFavorites();
          const favoritesList = result?.favorites || [];
          const isFav = favoritesList.some(p => p.id === property.id);
          setIsFavorite(isFav);
        } catch (error) {
          console.error('Error checking favorite status:', error);
          // Fallback to false on error
          setIsFavorite(false);
        }
      } else {
        // Check local storage
        const isFav = isLocalFavorite(property.id);
        setIsFavorite(isFav);
      }
    };

    checkFavoriteStatus();
  }, [property.id, isAuthenticated]);

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsLoading(true);
    try {
      const result = await toggleFavorite(property.id, isAuthenticated);

      if (result && typeof result.isFavorite === 'boolean') {
        setIsFavorite(result.isFavorite);

        if (result.isFavorite) {
          toast.success(t('favorites.added') || 'Added to favorites!');
        } else {
          toast.info(t('favorites.removed') || 'Removed from favorites');
        }
      } else {
        throw new Error('Invalid response from toggle favorite');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(t('favorites.error') || 'Failed to update favorites');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className={compact ? 'border-0' : 'h-100 border-0 shadow-sm'} style={{ overflow: 'hidden', transition: 'all 0.3s ease' }}>
      <div className="position-relative">
        <Link to={`/properties/${property.id}`} className="text-decoration-none">
          {primaryImage ? (
            <Card.Img
              variant="top"
              as={PropertyThumbnail}
              src={primaryImage}
              width={compact ? 300 : 400}
              height={compact ? 200 : 300}
              alt={property.title}
              style={{ height: compact ? '180px' : '250px', objectFit: 'cover' }}
            />
          ) : (
            <div
              className="bg-secondary bg-opacity-25 d-flex align-items-center justify-content-center text-muted"
              style={{ height: compact ? '180px' : '250px' }}
            >
              {t('property.noImage')}
            </div>
          )}
        </Link>

        {showDistance && property.distance_km && (
          <Badge
            bg="dark"
            className="position-absolute top-0 start-0 m-3"
            style={{ fontSize: '0.85rem' }}
          >
            📍 {property.distance_km} km
          </Badge>
        )}

        {!compact && (
          <Button
            variant="light"
            size="sm"
            className={`position-absolute top-0 end-0 m-3 rounded-circle p-2 ${isFavorite ? 'text-danger' : ''}`}
            onClick={handleToggleFavorite}
            disabled={isLoading}
            aria-label={isFavorite ? t('favorites.removeFromFavorites') : t('favorites.addToFavorites')}
            style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            {isFavorite ? <FaHeart size={18} /> : <FiHeart size={18} />}
          </Button>
        )}
      </div>

      <Card.Body style={compact ? { padding: '12px' } : {}}>
        <Link to={`/properties/${property.id}`} className="text-decoration-none text-dark">
          <div className="d-flex align-items-center text-muted mb-2" style={{ fontSize: compact ? '0.8rem' : '0.9rem' }}>
            <FiMapPin size={14} className="me-1" />
            <span>{property.city}, {property.country}</span>
          </div>

          <Card.Title className="h6 mb-2" style={{ fontSize: compact ? '0.95rem' : '1.1rem', fontWeight: '600' }}>
            {property.title}
          </Card.Title>

          {!compact && (
            <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
              <span>{property.bedrooms} {property.bedrooms === 1 ? t('property.bed') : t('property.beds')}</span>
              <span className="mx-1">·</span>
              <span>{property.bathrooms} {property.bathrooms === 1 ? t('property.bath') : t('property.baths')}</span>
              <span className="mx-1">·</span>
              <span>{property.max_guests} {property.max_guests === 1 ? t('property.guest') : t('property.guests')}</span>
            </div>
          )}

          {!compact && showHost && property.host && (
            <div className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
              {t('property.hostedBy')} {property.host.first_name}
            </div>
          )}

          <div className={`d-flex justify-content-between align-items-center ${compact ? 'mt-2' : 'mt-3'}`}>
            <div>
              {property.average_rating > 0 ? (
                <div className="d-flex align-items-center gap-1">
                  <span>⭐</span>
                  <span className="fw-semibold" style={{ fontSize: compact ? '0.85rem' : '1rem' }}>
                    {property.average_rating.toFixed(1)}
                  </span>
                  {!compact && (
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      ({property.total_reviews})
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-muted" style={{ fontSize: compact ? '0.75rem' : '0.85rem' }}>
                  {t('property.newListing')}
                </span>
              )}
            </div>
            <div className="text-end">
              <div>
                <span className="fw-bold" style={{ fontSize: compact ? '0.95rem' : '1.1rem' }}>
                  R$ {property.price_per_night}
                </span>
                <span className="text-muted ms-1" style={{ fontSize: compact ? '0.75rem' : '0.85rem' }}>
                  {t('property.perNight')}
                </span>
              </div>
            </div>
          </div>
        </Link>
      </Card.Body>
    </Card>
  );
};

export default PropertyCard;
